import { useCallback } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import theme from "theme";
import { useRedirectUponFetchFailure } from "utils/common";

import Stack from "components/Stack";
import toast from "components/Toast";
import { FiltersContextProvider } from "components/useFilters";
import { usePageTitle } from "components/usePageTitle";

import CustomViewContent from "pages/Reporting/CustomReport/CustomViewContent";
import CustomReportPageHeader from "pages/Reporting/CustomReport/PageHeader";

import { useReportPackageDetailsInvalidateSucceeded } from "entities/Reporting/hooks";
import {
  useCustomReportCompanyDeals,
  useReportPackageDetails,
} from "entities/Reporting/sdk";

const CustomReport = () => {
  const params = useParams<{ customReportId: string }>();
  const customReportId = Number(params.customReportId);

  const {
    data: reportPackage,
    error: reportPackageError,
    loading: isLoadingReportPackageDetails,
  } = useReportPackageDetails(customReportId);

  useRedirectUponFetchFailure({
    error: reportPackageError,
    loading: isLoadingReportPackageDetails,
  });

  usePageTitle(`Builders Patch: Reports - ${_.startCase(reportPackage?.name)}`);

  const {
    columns = [],
    rows = [],
    latestRowUpdateDateTime,
    loading: isLoadingCustomReportCompanyDeals,
    isValidating,
    error: customReportCompanyDealsError,
    mutate: refetchCustomReportCompanyDeals,
  } = useCustomReportCompanyDeals(customReportId);

  useRedirectUponFetchFailure({
    error: customReportCompanyDealsError,
    loading: isLoadingCustomReportCompanyDeals,
  });

  const reportPackageDetailsInvalidateSucceededEventHandler = useCallback(
    ({
      reportPackageId,
    }: {
      reportPackageId: number;
      reportPackageName: string;
    }) => {
      if (reportPackageId === customReportId) {
        refetchCustomReportCompanyDeals();
        toast.successMessage("Report has been updated successfully!");
      }
    },
    [customReportId, refetchCustomReportCompanyDeals]
  );

  useReportPackageDetailsInvalidateSucceeded({
    eventHandler: reportPackageDetailsInvalidateSucceededEventHandler,
  });

  if (_.isUndefined(reportPackage)) {
    return null;
  }

  return (
    <FiltersContextProvider key={customReportId}>
      <Stack
        spacing={2}
        sx={{ padding: theme.spacing(0, 3), flex: 1 }}
        data-testid="page-content"
      >
        <CustomReportPageHeader
          reportPackage={reportPackage}
          rows={rows}
          columns={columns}
          loading={isLoadingCustomReportCompanyDeals}
          latestRowUpdateDateTime={latestRowUpdateDateTime}
        />
        <CustomViewContent
          rows={rows}
          columns={columns}
          loading={isLoadingCustomReportCompanyDeals}
          isValidating={isValidating}
          reportPackage={reportPackage}
        />
      </Stack>
    </FiltersContextProvider>
  );
};

export default CustomReport;
